import React from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';
import { QueueSubmissionProcessingResult } from '~/types/fundrequests';

interface ProcessingResultsModalProps {
    isOpen: boolean;
    onClose: () => void;
    processingResult: QueueSubmissionProcessingResult;
    isError: boolean;
    message?: string;
    error?: string;
}

const ProcessingResultsModal: React.FC<ProcessingResultsModalProps> = ({
    isOpen,
    onClose,
    processingResult,
    isError,
    message,
    error,
}) => {
    if (!isOpen) return null;

    const { successful_count, failed_count, successful_requests, failed_requests, total_requests, payment_type } = processingResult;

    const getHeaderIcon = () => {
        if (isError || failed_count === total_requests) {
            return <FaTimesCircle className="text-red-500" size={24} />;
        } else if (failed_count > 0) {
            return <FaExclamationTriangle className="text-yellow-500" size={24} />;
        } else {
            return <FaCheckCircle className="text-green-500" size={24} />;
        }
    };

    const getHeaderText = () => {
        if (isError || failed_count === total_requests) {
            return 'Queue Processing Failed';
        } else if (failed_count > 0) {
            return 'Queue Processing Partially Successful';
        } else {
            return 'Queue Processing Successful';
        }
    };

    const getHeaderClass = () => {
        if (isError || failed_count === total_requests) {
            return 'text-red-700';
        } else if (failed_count > 0) {
            return 'text-yellow-700';
        } else {
            return 'text-green-700';
        }
    };

    return (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
            <div className='flex h-auto max-h-[90vh] w-[800px] max-w-4xl flex-col rounded-lg bg-white p-6 shadow-lg'>
                <div className='flex-grow overflow-auto'>
                    {/* Header */}
                    <div className='flex items-center gap-3 mb-4'>
                        {getHeaderIcon()}
                        <h2 className={`text-2xl font-semibold ${getHeaderClass()}`}>
                            {getHeaderText()}
                        </h2>
                    </div>

                    {/* Message or Error */}
                    <div className='mb-4'>
                        {message && (
                            <p className='text-gray-700 mb-2'>{message}</p>
                        )}
                        {error && (
                            <p className='text-red-700 mb-2'>{error}</p>
                        )}
                    </div>

                    {/* Summary Stats */}
                    <div className='grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg'>
                        <div className='text-center'>
                            <div className='text-2xl font-bold text-gray-900'>{total_requests}</div>
                            <div className='text-sm text-gray-600'>Total Requests</div>
                        </div>
                        <div className='text-center'>
                            <div className='text-2xl font-bold text-green-600'>{successful_count}</div>
                            <div className='text-sm text-gray-600'>Successful</div>
                        </div>
                        <div className='text-center'>
                            <div className='text-2xl font-bold text-red-600'>{failed_count}</div>
                            <div className='text-sm text-gray-600'>Failed</div>
                        </div>
                    </div>

                    {/* Results Tables */}
                    <div className='max-h-[50vh] overflow-auto'>
                        {/* Successful Requests */}
                        {successful_requests.length > 0 && (
                            <div className='mb-6'>
                                <h3 className='text-lg font-semibold text-green-700 mb-3 flex items-center gap-2'>
                                    <FaCheckCircle size={16} />
                                    Successful Requests ({successful_count})
                                </h3>
                                <div className='border rounded-lg overflow-hidden'>
                                    <table className='w-full table-auto'>
                                        <thead className='bg-green-50'>
                                            <tr>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>ID</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Description</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Vendor</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Patient</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Document ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {successful_requests.map((request, index) => (
                                                <tr key={index} className='border-t border-gray-200'>
                                                    <td className='px-4 py-2 text-sm'>{request.fund_request_id}</td>
                                                    <td className='px-4 py-2 text-sm'>{request.fund_request_description}</td>
                                                    <td className='px-4 py-2 text-sm'>{request.vendor_name}</td>
                                                    <td className='px-4 py-2 text-sm'>{request.patient_name}</td>
                                                    <td className='px-4 py-2 text-sm font-mono text-xs'>{request.document_id}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Failed Requests */}
                        {failed_requests.length > 0 && (
                            <div className='mb-6'>
                                <h3 className='text-lg font-semibold text-red-700 mb-3 flex items-center gap-2'>
                                    <FaTimesCircle size={16} />
                                    Failed Requests ({failed_count})
                                </h3>
                                <div className='border rounded-lg overflow-hidden'>
                                    <table className='w-full table-auto'>
                                        <thead className='bg-red-50'>
                                            <tr>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>ID</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Description</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Vendor</th>
                                                <th className='px-4 py-2 text-left text-sm font-medium text-gray-700'>Patient</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {failed_requests.map((request, index) => (
                                                <React.Fragment key={index}>
                                                    <tr className='border-t border-gray-200 hover:bg-gray-50'>
                                                        <td className='px-4 py-2 text-sm'>{request.fund_request_id}</td>
                                                        <td className='px-4 py-2 text-sm'>{request.fund_request_description}</td>
                                                        <td className='px-4 py-2 text-sm'>{request.vendor_name}</td>
                                                        <td className='px-4 py-2 text-sm'>{request.patient_name}</td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={4} className='px-0 py-0'>
                                                            <div className='mx-4 mb-3 mt-1 p-3 rounded-md shadow-sm' style={{ backgroundColor: '#fef2f2', border: '2px solid #f87171', borderLeft: '6px solid #dc2626' }}>
                                                                <div className='flex items-start gap-3'>
                                                                    <div className='flex-shrink-0 mt-0.5'>
                                                                        <div className='w-6 h-6 rounded-full flex items-center justify-center' style={{ backgroundColor: '#dc2626', boxShadow: '0 0 0 3px #fca5a5' }}>
                                                                            <FaTimesCircle className='text-white' size={14} />
                                                                        </div>
                                                                    </div>
                                                                    <div className='flex-1'>
                                                                        <div className='text-sm font-bold mb-1 uppercase tracking-wide' style={{ color: '#7f1d1d' }}>
                                                                            ⚠️ Processing Error
                                                                        </div>
                                                                        <div className='text-sm leading-relaxed font-medium p-2 rounded' style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #f87171' }}>
                                                                            {request.error}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className='mt-4 flex justify-end'>
                    <button
                        onClick={onClose}
                        className='rounded-full border border-black px-6 py-2 text-black hover:bg-gray-50'
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProcessingResultsModal; 